import { useQuery } from '@apollo/client';
import { Autocomplete, Box, Divider, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '~/contexts';
import { useDebounce } from '~/lib/debounce';
import { DeviceValuesPreviewDevicesDocument } from './DeviceValuesPreview.generated';

export interface DeviceValuesPreviewProps {
  onPreviewDeviceChange: (deviceId: string) => void;
  previewDeviceId: string;
  /** Resolved by the same lookup the canvas uses, so the caption cannot disagree with it. */
  previewValue?: string;
}

interface DeviceOption {
  id: number;
  name: string;
}

/**
 * Picks the device whose values the canvas shows. Lists the network's devices by name; the
 * field itself holds the device ID, which is what the source's device key column matches.
 */
export const DeviceValuesPreview = ({
  onPreviewDeviceChange,
  previewDeviceId,
  previewValue,
}: DeviceValuesPreviewProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(previewDeviceId);

  // Typed one character at a time, so wait for a pause rather than querying every keystroke.
  const search = useDebounce(inputValue.trim(), 300);

  // This query only feeds the list, so nothing is fetched until it is opened.
  const { data, loading } = useQuery(DeviceValuesPreviewDevicesDocument, {
    skip: !open,
    variables: { networkId: currentNetwork.id, search: search || undefined },
  });

  const options: ReadonlyArray<DeviceOption> = data?.network?.devices.nodes ?? [];

  return (
    <>
      <Divider />
      <Box
        sx={{
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1.5,
        }}
      >
        <Typography color="text.secondary" sx={{ fontSize: '0.7rem' }} variant="overline">
          {t('components.data_item_cell_select.fields.preview_as_device')}
        </Typography>
        <Autocomplete
          filterOptions={(x) => x}
          freeSolo
          getOptionLabel={(option) => (typeof option === 'string' ? option : String(option.id))}
          inputValue={inputValue}
          loading={loading}
          onChange={(_, value) => {
            // Picking a device previews it; clearing the field clears the preview.
            if (value && typeof value !== 'string') onPreviewDeviceChange(String(value.id));
            if (!value) onPreviewDeviceChange('');
          }}
          onClose={() => setOpen(false)}
          onInputChange={(_, value) => {
            setInputValue(value);
            // A typed ID previews too, so a device not in the list can still be checked.
            if (/^\d*$/.test(value.trim())) onPreviewDeviceChange(value.trim());
          }}
          onOpen={() => setOpen(true)}
          open={open}
          options={options}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('components.data_item_cell_select.fields.preview_device_placeholder')}
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: 'flex', gap: 1 }}>
              <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>
                {option.name}
              </Box>
              <Box
                component="span"
                sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
              >
                {option.id}
              </Box>
            </Box>
          )}
        />
        <Typography color="text.secondary" variant="caption">
          {previewValue !== undefined
            ? t('components.data_item_cell_select.fields.preview_showing', {
                value: previewValue,
              })
            : t('components.data_item_cell_select.fields.preview_placeholder')}
        </Typography>
      </Box>
    </>
  );
};
